import { toValue } from 'vue'

type PersistedFetchOpts = {
  key?: string
  watch?: any[]
  server?: boolean
  headers?: Record<string, string> | (() => Record<string, string>)
  credentials?: RequestCredentials
  lazy?: boolean
}

export async function usePersistedFetch<
  TData = any,
  TVars extends Record<string, any> = Record<string, any>,
>(
  operationName: string,
  variables: TVars = {} as TVars,
  opts: PersistedFetchOpts = {},
) {
  const { locale } = useI18n()
  const runtimeConfig = useRuntimeConfig()
  const apiUrl = runtimeConfig.public.httpEndpoint

  const resolveHeaders = () =>
    typeof opts.headers === 'function' ? opts.headers() : opts.headers || {}

  const getCleanVariables = () => {
    const resolved: Record<string, any> = {}
    for (const key in variables) {
      resolved[key] = toValue(variables[key])
    }
    return resolved
  }

  const { data, pending, error, refresh } = await useAsyncData<TData>(
    () => {
      const prefix = opts.key || `pqfetch:${operationName}`
      return `${prefix}:${locale.value}:${JSON.stringify(getCleanVariables())}`
    },
    async () => {
      const res: any = await $fetch(apiUrl, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          'content-language': locale.value,
          ...resolveHeaders(),
        },
        body: {
          operationName,
          queryId: operationName,
          variables: getCleanVariables(),
        },
        credentials: opts.credentials ?? 'include',
      })

      if (res?.errors?.length) throw res
      return res?.data as TData
    },
    {
      watch: [
        locale,
        () => JSON.stringify(getCleanVariables()),
        ...(opts.watch || []),
      ],
      server: opts.server ?? true,
      lazy: opts.lazy ?? false,
    },
  )

  return { data, pending, error, refresh }
}
